import { DatePipe } from "@angular/common";
import {
    AfterContentInit,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { FileUploadComponent , FileBase64 } from "@components/file-upload/file-upload.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TranslateDirective, TranslateService } from "@ngx-translate/core";

import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, RaskRoller } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { Utils } from "~app/common/services/utils";
import { VirkeskopDokumentInfoDTO } from "~app/features/virkeskop/dto/dokument/VirkeskopDokumentInfoDTO";
import { KontraktDokumentService } from "~app/features/virkeskop/services/kontrakt-dokument.service";
import { SaveVirkeskopsKontraktDokumentDTO } from "../../dto/dokument/SaveVirkeskopsKontraktDokumentDTO";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";

@Component({
    selector: "rask-kontrakt-dokument",
    templateUrl: "./kontrakt-dokument.component.html",
    styleUrls: ["./kontrakt-dokument.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateDirective,
        FileUploadComponent,
        NgbTooltip,
        ButtonComponent,
        DatePipe
    ]
})
export class KontraktDokumentComponent implements AfterContentInit, OnInit {
    readonly maxFileSize = 1024 * 1024 * 50;
    readonly maxFilenameLength = 50;

    acceptedFiletypes = [
        "xls",
        "xlsx",
        "doc",
        "docx",
        "pdf",
        "txt",
        "msg",
        "jpg",
        "jpeg",
        "png"
    ];
    @Input() atgardId: number;
    @Input() deleteFileEnabled = true;
    dokument: VirkeskopDokumentInfoDTO[] = [];
    @Input({ required: true }) dokumenttyp: string;
    fileActionInProgress = false;
    fileName = "";
    @Output() fileUpload = new EventEmitter();
    fileUploadError = "";
    @Input() isForKontrakt = true;
    @Input() placeholderText: string;
    @Input() readonly = false;
    @Input() requireAtLeastOneFile = false;
    @Input() multiple = false;
    files: FileBase64[] = [];

    private readonly authenticationService = inject(AuthenticationService);
    private readonly kontraktDokumentService = inject(KontraktDokumentService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly modalDialogService = inject(RaskModalService);
    private readonly translate = inject(TranslateService);

    get deleteToolTip() {
        if (this.isLastFileDeletionDisabled) {
            return "Minst en fil måste finnas i status tillgänglig";
        }
        return null;
    }

    get hasEditFilePermission() {
        return this.authenticationService.hasAnyRole([
            RaskRoller.role.Kopleda,
            RaskRoller.role.Virkeskopa,
            RaskRoller.role.Skogsvardsuppdragsplanera
        ]);
    }

    get hasUploadFilePermission() {
        return this.authenticationService.hasAnyRole([
            RaskRoller.role.Kopleda,
            RaskRoller.role.Virkeskopa,
            RaskRoller.role.Kopplanera,
            RaskRoller.role.Skogsvardsuppdragsplanera
        ]);
    }

    get hasViewFilePermission() {
        return this.authenticationService.hasAnyRole([
            RaskRoller.role.Titta,
            RaskRoller.role.Skogsvardsuppdragsplanera
        ]);
    }

    get isLastFileDeletionDisabled() {
        return this.requireAtLeastOneFile && this.dokument.length === 1;
    }

    get kontraktId(): number {
        return this.kontraktMediatorService.kontrakt.id;
    }

    get uploadButtonInactive() {
        return this.fileActionInProgress || this.fileName.length <= 0;
    }

    deleteDokument(dokumentInfo: VirkeskopDokumentInfoDTO) {
        if (this.readonly) {
            return;
        }
        this.modalDialogService.confirm(new ModalSettings(this.translate.instant("VIRKESKOP_ANBUD_KONTRAKT_TILLAGDA_FILER_TA_BORT_BILAGA"), dokumentInfo.filnamn))
            .subscribe(modalResult => {
                if (modalResult === ConfirmDialogResponse.save && dokumentInfo.dokumentnyckel &&
                    this.dokument.findIndex(b => b.dokumentnyckel === dokumentInfo.dokumentnyckel) > -1 &&
                    !this.fileActionInProgress) {
                    this.fileActionInProgress = true;

                    this.kontraktDokumentService.deleteDokument(dokumentInfo.virkeskopskontraktId, dokumentInfo.id).then(response => {
                        this.dokument.splice(this.dokument.findIndex(b => b.dokumentnyckel === dokumentInfo.dokumentnyckel), 1);
                    }).catch(err => {
                        this.fileUploadError = err;
                    }).finally(() => {
                        this.fileActionInProgress = false;
                    });
                }
            });
    }

    ngAfterContentInit(): void {
        if (this.kontraktId && this.isForKontrakt) {
            // i framtida US för uppladdning mot kontrakt, hämta för kontraktet i kontraktDokumentService.getDokumentForKontrakt
        } else if (!this.isForKontrakt && this.atgardId && this.hasViewFilePermission) {
            this.kontraktDokumentService.getDokumentForAtgard(this.atgardId, this.dokumenttyp)
                .then(response => {
                    if (response.length > 0) {
                        response.forEach(d => {
                            const convertedDate = new Date(d.skapadTidpunkt);
                            this.dokument.push({
                                id: d.id,
                                virkeskopskontraktId: d.virkeskopskontraktId,
                                virkeskopsatgardId: d.virkeskopsatgardId,
                                filnamn: d.filnamn,
                                dokumentnyckel: d.dokumentnyckel,
                                version: d.version,
                                skapadTidpunkt: `${convertedDate.toLocaleDateString("sv-SE")} ${convertedDate.toLocaleTimeString("sv-SE")}`,
                                dokumenttyp: d.dokumenttyp
                            });
                        });
                    }
                });
        }
    }

    ngOnInit(): void {
        this.placeholderText = this.translate.instant("VIRKESKOP_ANBUD_KONTRAKT_TILLAGDA_FILER_LAGG_TILL_BILAGA");
    }

    async onFileSelected(files: FileBase64[]) {
        this.fileUploadError = "";

        files.forEach(f => {
            const fileExtension = f.name.split(".").pop();
            if (this.acceptedFiletypes.indexOf(fileExtension.toLowerCase()) === -1) {
                this.fileUploadError = this.translate.instant("VIRKESKOP_ANBUD_KONTRAKT_TILLAGDA_FILER_TILLATNA_FILTYPER");
            } else if (f.size > this.maxFileSize) {
                this.fileUploadError = this.translate.instant("VIRKESKOP_ANBUD_KONTRAKT_TILLAGDA_FILER_MAX_UPLOAD_STORLEK");
            } else if (f.name.length > this.maxFilenameLength) {
                this.fileUploadError = this.translate.instant("VIRKESKOP_ANBUD_KONTRAKT_TILLAGDA_FILER_MAX_FILNAMN_STORLEK");
            }
        });

        if (this.fileUploadError?.length > 0) {
            this.clearFileInput();
        } else {
            this.files = files;
            this.fileName = files.map(f => f.name).join(", ");
        }
    }

    openBilaga(kontraktId: number, dokumentId: number) {
        this.kontraktDokumentService.getDokumentForKontraktDownload(kontraktId, dokumentId).subscribe((blob: Blob) => {
            const url = URL.createObjectURL(blob);
            window.open(url);
        });
        return false;
    }

    downloadDokument(dokument: VirkeskopDokumentInfoDTO) {
        this.kontraktDokumentService.getDokumentForKontraktDownload(dokument.virkeskopskontraktId, dokument.id).subscribe((blob: Blob) => {
            Utils.downloadBlob(blob, dokument.filnamn);
        });
        return false;
    }

    uploadDocument() {
        if (this.readonly) {
            return;
        }
        this.fileActionInProgress = true;
        if (this.files.length === 0) {
            return;
        }

        const dokument: SaveVirkeskopsKontraktDokumentDTO = {
            kontraktId: this.kontraktId,
            atgardId: this.atgardId,
            dokumenttyp: this.dokumenttyp,
            files: this.files
        };

        this.kontraktDokumentService.uploadDokumentAsync(dokument).then(response => {
            this.clearFileInput();
            response.forEach(d => {
                const index = this.dokument.findIndex(dok => dok.filnamn === d.filnamn);
                if (index > -1) {
                    {
                        this.dokument = [
                            ...this.dokument.slice(0, index),
                            d,
                            ...this.dokument.slice(index + 1)
                        ];
                    }
                } else {
                    this.dokument = [
                        ...this.dokument,
                        d
                    ];
                }
            });

            this.fileUpload.emit();
        }).catch(err => {
            this.fileUploadError = err;
        }).finally(() => {
            this.clearFileInput();
            this.fileActionInProgress = false;
        });
    }

    private readonly clearFileInput = () => {
        this.files = [];
        this.fileName = "";
    };
}
